import { useEffect, useRef, useState } from "react";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PIShell, Drill, PIInsight, PracticeLadder } from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { spellDegree } from "@/theory/spell";
import { playRhythm, setRhythmBpm, RhythmHandle } from "@/audio/rhythm";

/**
 * Piano Intermediate · Lesson 11 — Basic Finger Independence Exercise.
 *
 * Jason's own drill, on the first five notes of a major or minor scale. One finger
 * holds its note for the whole bar (the anchor); the finger at the other end plays
 * every "and"; the middle three walk on beats 2, 3 and 4. Then the hand flips: the
 * pinky anchors and the thumb takes the "ands".
 *
 * Source: his handwritten pages for this lesson (book pages 11, and the
 * "Finger Independence Exercise" sheet) and the lesson video's transcript:
 *   thumb anchors 1 →  5 4 5 3 5 2 5  |  5 2 5 3 5 4 5
 *   pinky anchors 5 →  1 2 1 3 1 4 1  |  1 4 1 3 1 2 1
 * Each row is the seven eighths after the anchor's downbeat (1 & 2 & 3 & 4 &).
 */

type Anchor = "thumb" | "pinky";

/** Scale degrees on the eighths "&, 2, &, 3, &, 4, &" — two bars per anchor. */
const PATTERNS: Record<Anchor, [number[], number[]]> = {
  thumb: [[5, 4, 5, 3, 5, 2, 5], [5, 2, 5, 3, 5, 4, 5]],
  pinky: [[1, 2, 1, 3, 1, 4, 1], [1, 4, 1, 3, 1, 2, 1]],
};
const FINGER = ["", "thumb", "index", "middle", "ring", "pinky"];
const FINGER_SHORT = ["", "t", "i", "m", "r", "p"];
const COUNTS = ["1", "&", "2", "&", "3", "&", "4", "&"];

export default function PianoIntermediate11() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "11");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="G" availableQualities={["major", "natural-minor"]}>
        <PIShell
          title="Basic Finger Independence"
          exerciseGoal="One finger holds its note for the whole bar while the other four keep moving — first the thumb anchors, then the pinky. Five notes, major and minor."
          bpm={80}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const [anchor, setAnchor] = useState<Anchor>("thumb");
  const [bpm, setBpm] = useState(80);
  const [leftHand, setLeftHand] = useState(true);
  const [step, setStep] = useState(-1);
  const [running, setRunning] = useState(false);
  const handleRef = useRef<RhythmHandle | null>(null);

  // Five-finger position from the tonic, kept around middle C: G sits at G4, C at C4.
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const base = tonicMidi < 60 ? tonicMidi + 12 : tonicMidi;
  const minor = k.quality !== "major";
  const SEMIS = minor ? [0, 0, 2, 3, 5, 7] : [0, 0, 2, 4, 5, 7]; // index = scale degree 1..5
  const midiOf = (deg: number) => base + SEMIS[deg];
  const nameOf = (deg: number) => k.nameForDegree(deg);
  const anchorDeg = anchor === "thumb" ? 1 : 5;

  const stop = () => {
    handleRef.current?.stop();
    handleRef.current = null;
    setRunning(false);
    setStep(-1);
  };
  useEffect(() => () => handleRef.current?.stop(), []);
  useEffect(() => { if (running) setRhythmBpm(bpm, 0.05); }, [bpm, running]);
  // A new key, mode or anchor means a new exercise: stop the old one.
  useEffect(() => { stop(); }, [k.tonicPc, k.quality, anchor, leftHand]); // eslint-disable-line react-hooks/exhaustive-deps

  const play = async () => {
    if (running) { stop(); return; }
    const events = [];
    const name = (m: number) => midiToPitchName(m, k.useFlats);
    for (let bar = 0; bar < 2; bar++) {
      const at = bar * 8;
      // The anchor: struck on the downbeat and held for the whole bar.
      events.push({ beat: at, notes: [name(midiOf(anchorDeg))], durationSteps: 8, velocity: 0.75 });
      PATTERNS[anchor][bar].forEach((deg, i) => {
        events.push({ beat: at + 1 + i, notes: [name(midiOf(deg))], durationSteps: 1, velocity: deg === (anchor === "thumb" ? 5 : 1) ? 0.62 : 0.8 });
      });
      // Left hand: a plain pulse on the tonic — "you need to do something in the left hand".
      if (leftHand) {
        for (const b of [0, 4]) events.push({ beat: at + b, notes: [name(base - 24)], durationSteps: 4, velocity: 0.5 });
      }
    }
    handleRef.current = await playRhythm(events, {
      bpm, subdivide: 2, totalSteps: 16, loop: true, click: false, beatsPerBar: 4,
      onStep: (s) => setStep(s),
    });
    setRunning(true);
  };

  const bar = step >= 8 ? 1 : 0;
  const inBar = step >= 0 ? step % 8 : -1;
  const sounding = inBar > 0 ? PATTERNS[anchor][bar][inBar - 1] : null;

  const highlights: KeyHighlight[] = [
    { midi: midiOf(anchorDeg), tone: "root", label: anchor === "thumb" ? "1·t" : "5·p" },
    ...(sounding ? [{ midi: midiOf(sounding), tone: "voice" as const, label: FINGER_SHORT[sounding] }] : []),
  ];

  return (
    <>
      <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-4">
        <KeyPicker />
        <p className="mb-3 text-[13px] text-cyan-100/80">
          In the video Jason plays this in C major and C minor. The player starts in G —
          pick C above to play along with him.
        </p>
        <PianoDiagram highlights={highlights} octaves={2} startOctave={Math.floor(base / 12) - 1} playable ariaLabel="Five-finger position with the anchor note lit" />

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-cyan-300/70">Anchor:</span>
          {(["thumb", "pinky"] as Anchor[]).map((a) => (
            <button
              key={a}
              onClick={() => setAnchor(a)}
              className={`rounded-md border px-3 py-1.5 font-mono text-[12px] font-bold uppercase ${
                anchor === a ? "border-amber-400/60 bg-amber-500/15 text-amber-200" : "border-cyan-400/30 bg-cyan-500/5 text-cyan-200 hover:bg-cyan-500/15"
              }`}
            >
              {a === "thumb" ? `Thumb holds ${nameOf(1)}` : `Pinky holds ${nameOf(5)}`}
            </button>
          ))}
          <label className="ml-2 flex items-center gap-2 text-[12px] text-cyan-200">
            <input type="checkbox" checked={leftHand} onChange={(e) => setLeftHand(e.target.checked)} className="h-4 w-4 accent-amber-400" />
            Left-hand pulse
          </label>
        </div>

        {/* The two bars, as Jason writes them: counts on top, notes and fingers below */}
        <div className="mt-4 space-y-3">
          {PATTERNS[anchor].map((row, b) => (
            <div key={b} className="overflow-x-auto">
              <div className="grid min-w-[480px] grid-cols-8 gap-1">
                {COUNTS.map((c, i) => (
                  <div key={i} className="text-center font-mono text-[11px] text-cyan-300/70">{c}</div>
                ))}
                {COUNTS.map((_, i) => {
                  const deg = i === 0 ? anchorDeg : row[i - 1];
                  const isNow = running && bar === b && inBar === i;
                  const isAnchor = i === 0;
                  return (
                    <div
                      key={i}
                      className={`flex h-14 flex-col items-center justify-center rounded-md border text-center transition ${
                        isAnchor ? "border-amber-400/60 bg-amber-500/15 text-amber-100" : "border-cyan-400/20 bg-cyan-500/5 text-cyan-50"
                      } ${isNow ? "ring-2 ring-amber-300" : ""}`}
                    >
                      <span className="text-[15px] font-semibold">{nameOf(deg)}</span>
                      <span className="font-mono text-[10px] opacity-70">{FINGER_SHORT[deg]}{isAnchor ? " · hold" : ""}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            onClick={play}
            className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
          >
            {running ? "■ Stop" : `▶ Play the drill in ${k.tonicName} ${minor ? "minor" : "major"}`}
          </button>
          <label className="flex items-center gap-2 text-[12px] text-cyan-200">
            <span className="font-mono uppercase tracking-wider text-cyan-300/70">Tempo</span>
            <input type="range" min={50} max={120} step={2} value={bpm} onChange={(e) => setBpm(Number(e.target.value))} className="w-32 accent-amber-400" />
            <span className="w-16 font-mono">{bpm} bpm</span>
          </label>
        </div>
      </div>

      <Drill number="1" label="The idea" title="Finger independence, not just hand independence">
        <p>
          We usually talk about one hand doing something while the other hand does something
          else. This drill works inside one hand: one finger holds its note down for the whole
          bar while the other four keep moving. It uses only the first five notes of the scale —
          {" "}{[1, 2, 3, 4, 5].map(nameOf).join(" ")} here — in major, then in minor.
        </p>
      </Drill>

      <Drill number="2" label="Thumb anchors" title={`Hold ${nameOf(1)}, the pinky takes every "and"`}>
        <p>
          The thumb strikes {nameOf(1)} on beat 1 and keeps it down. The pinky plays {nameOf(5)} on
          every "and". In between, the middle fingers walk down on beats 2, 3 and 4 —
          {" "}{nameOf(4)}, {nameOf(3)}, {nameOf(2)} — and in the second bar back up:
          {" "}{nameOf(2)}, {nameOf(3)}, {nameOf(4)}.
        </p>
        <PIInsight>
          Count it out loud: "1 and 2 and 3 and 4 and". The pinky lives on every "and"; the
          ring, middle and index fingers live on 2, 3 and 4.
        </PIInsight>
      </Drill>

      <Drill number="3" label="Pinky anchors" title="Flip the whole exercise around">
        <p>
          Now the pinky holds {nameOf(5)} and the thumb takes over the "ands" on {nameOf(1)}. The
          middle fingers do the same walk: {nameOf(2)}, {nameOf(3)}, {nameOf(4)}, then
          {" "}{nameOf(4)}, {nameOf(3)}, {nameOf(2)}. Whatever the thumb did as the anchor, the
          pinky now does.
        </p>
      </Drill>

      <Drill number="4" label="Minor" title="Lower the third">
        <p>
          The minor version changes one note: the middle finger's {spellDegree(k.tonicName, "3")} drops
          a half step to {spellDegree(k.tonicName, "♭3")}. Everything else stays where it was. Switch
          the picker above to minor and play both anchors again.
        </p>
      </Drill>

      <Drill number="5" label="Left hand and variations" title="Don't leave the left hand idle">
        <ul className="my-2 space-y-1 text-[14px] text-cyan-50/95">
          <li>· Keep a steady pulse on the tonic (the player above does this).</li>
          <li>· Pick left-hand notes from the same five, so you never leave the key.</li>
          <li>· Harmonise: play a note a diatonic third below what the right hand is playing.</li>
          <li>· Float the anchor: once the drill is clean, move the held finger to a neighbouring note and back.</li>
          <li>· Sing it: hum the anchor note, then the "and" note, while you play.</li>
        </ul>
      </Drill>

      <Drill number="6" label="Practice ladder" title="Twelve minutes">
        <PracticeLadder
          intro={<>Slow and even first. If a finger slips, slow down — that is the finger losing control, not the tempo being too low.</>}
          rungs={[
            { title: "Thumb anchors, major", minutes: 3, accent: "amber", detail: "Both bars, slowly, counting out loud." },
            { title: "Pinky anchors, major", minutes: 3, accent: "cyan", detail: "Flip it. The thumb now plays every \"and\"." },
            { title: "Both anchors, minor", minutes: 3, accent: "rose", detail: "Lower the third. Same fingers, same walk." },
            { title: "Add the left hand", minutes: 3, accent: "emerald", detail: "A steady pulse, then a third below the right hand." },
          ]}
        />
      </Drill>
    </>
  );
}
